import { isPageEnabled } from "./navigation/page-availability";
import { Component, NgZone, OnInit, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { AlertController, MenuController, NavController, Platform } from "@ionic/angular";

import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../environments/environment";
import { GeneralSettingsModel, navSourceEnum } from "./models";
import { DEV_BYPASS_AIRCRAFT, resolveDevBypassAircraft } from "./plugins/opsctrl-foc/class/providers/fppProvider";
import { fccProvider } from "./plugins/opsctrl-foc/class/providers/fccProvider";
import { classCom } from "./plugins/opsctrl-foc/focMain";
import { PLATFORM_ADAPTER } from "./core/platform/platform.adapter";
import { AuthService } from "./services/auth/auth.service";
import { ClientTokenService } from "./services/auth/client-token.service";
import { CrashRecoveryService } from "./services/crash-recovery/crash-recovery.service";
import { FlightTimerService } from "./services/flight-timer/flight-timer.service";
import { GeolocationService } from "./services/geolocation/geolocation.service";
import { IdleReloadService } from "./services/idle-reload/idle-reload.service";
import { NotificationsService } from "./services/notifications/notifications.service";
import { opsControlService } from "./services/ops-control/ops-control.service";
import { SentryContextService } from "./services/sentry-context/sentry-context.service";
import { SettingsService } from "./services/settings/settings.service";
import { NavService } from "./services/nav/nav.service";
import { UpdatesService } from "./services/updates/updates.service";
import { WebSessionBootstrapService } from "./services/auth/web-session-bootstrap.service";
import { WebSyncBootstrapService } from "./sync/web-sync-bootstrap.service";
import { AutoNavigService } from "./services/mission/auto-navig.service";
import { WallboardIdleService } from "./services/mission/wallboard-idle.service";
import { NativeSyncService } from "./sync/native-sync.service";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  private platform = inject(Platform);
  translate = inject(TranslateService);
  settings = inject(SettingsService);
  private geolocation = inject(GeolocationService);
  private flightTimerCtrl = inject(FlightTimerService);
  private crashRecovery = inject(CrashRecoveryService);
  private alert = inject(AlertController);
  private auth = inject(AuthService);
  private navCtrl = inject(NavController);
  private menu = inject(MenuController);

  public menuPinned = false;
  /** The user closed the pinned menu; it then opens as an overlay at any width. */
  public menuCollapsed = readMenuCollapsed();

  public setMenuCollapsed(collapsed: boolean): void {
    this.menuCollapsed = collapsed;
    try {
      localStorage.setItem(MENU_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage blocked: the choice lasts until the page reloads.
    }
    if (!collapsed) this.menu.close().catch((err) => console.error("menuClose: ", err));
  }

  public closeMenu(): void {
    if (this.menuPinned) return;
    this.menu.close().catch((err) => console.error("menuClose: ", err));
  }

  // The menu navigates in code and renders no href. Since @ionic/angular 8.8.12 a click with
  // Ctrl/Shift/Alt/Meta on a routerLink item goes to the browser, which opens a new tab or window.
  // Inside the mission-monitor iframe that took the EFB out of the wallboard.
  public openPage(url: string): void {
    this.navCtrl
      .navigateRoot([url], { queryParams: { entryMode: "files" } })
      .catch((err) => console.error("navigate: ", err));
  }

  public openDutyLimits(): void {
    this.navCtrl.navigateForward("/duty-ofp-limits").catch((err) => console.error("navigate: ", err));
  }

  public isPageActive(url: string): boolean {
    return this.router.isActive(url, {
      paths: "exact",
      queryParams: "ignored",
      fragment: "ignored",
      matrixParams: "ignored",
    });
  }

  public onSplitPaneChange(event: CustomEvent<{ visible: boolean }>): void {
    this.menuPinned = event.detail.visible;
    window.dispatchEvent(new Event("resize"));
  }
  private reportNavigation = inject(NavService);
  public menuReportLoading = false;
  opsControlSer = inject(opsControlService);
  private notifications = inject(NotificationsService);
  private update = inject(UpdatesService);
  // Eagerly instantiate so it starts pushing user/tag context to Sentry.
  private sentryContext = inject(SentryContextService);
  private webSync = inject(WebSyncBootstrapService);
  private autoNavig = inject(AutoNavigService);
  public wallboardIdle = inject(WallboardIdleService);
  // Native peer-sync orchestrator — eager so it wires its token/WS/apply
  // subscriptions at app start (constructor side-effect, gated to native).
  private nativeSync = inject(NativeSyncService);
  // WebSessionBootstrapService owns its own tokenObs subscription internally
  // (constructor side-effect). We inject here only to ensure the service is
  // instantiated at app start — providedIn:'root' is lazy until first inject.
  private webSessionBootstrap = inject(WebSessionBootstrapService);
  private platformAdapter = inject(PLATFORM_ADAPTER);
  private zone = inject(NgZone);
  private router = inject(Router);
  private clientToken = inject(ClientTokenService);
  private idleReload = inject(IdleReloadService);

  public numberOfNotifications: number = 0;

  public readonly isSidebarPageEnabled = (url: string): boolean => isPageEnabled(environment.pages, url);

  public appPages = [
    {
      numberBatch: 0,
      title: "base-briefing",
      url: "/base-briefing",
      section: "operations",
      icon: "location",
    },
    {
      numberBatch: 0,
      title: "flight briefing",
      url: "/flow-briefing",
      section: "operations",
      icon: "airplane",
    },
    {
      numberBatch: 0,
      title: "OFP archive",
      url: "/mission-page",
      section: "operations",
      icon: "document-attach",
    },
    {
      numberBatch: 0,
      title: "logbook",
      url: "/logbook/entries",
      section: "operations",
      icon: "book",
    },
    {
      numberBatch: 0,
      title: "flight records",
      url: "/flight-timer-list",
      section: "tools",
      icon: "hourglass",
    },
    {
      numberBatch: 0,
      title: "files",
      url: "/file-system",
      section: "tools",
      icon: "document",
    },
    {
      numberBatch: 0,
      title: "checklists",
      url: "/quick-docs",
      section: "tools",
      icon: "list-circle",
    },
    // disabled on PWA
    {
      title: "notifications",
      url: "/notifications",
      section: "tools",
      icon: "notifications",
    },
    {
      numberBatch: 0,
      title: "updates",
      url: "/updates",
      section: "tools",
      icon: "cloud-download",
    },
    {
      numberBatch: 0,
      title: "settings",
      url: "/settings",
      section: "tools",
      icon: "cog",
    },
  ];

  public get hasOperationsPages(): boolean {
    return this.appPages.some((p) => p.section === "operations" && this.isSidebarPageEnabled(p.url));
  }

  public get hasToolsPages(): boolean {
    return this.appPages.some((p) => p.section === "tools" && this.isSidebarPageEnabled(p.url));
  }

  private platformPausedAt: number = 0;
  private logoutAfterPlatformPausedHours: number = 12;

  public acftType: string;
  public generalSettings: GeneralSettingsModel;
  private navSource: number;

  /** `bound: false` = the device has no roster row and runs the fallback aircraft. */
  protected readonly bindingClaims = toSignal(this.clientToken.claimsObs, { initialValue: null });

  constructor() {
    effect(() => {
      if (this.auth.rebindPending()) void this.promptRebind();
    });
    effect(() => {
      if (this.auth.reloginNeeded()) void this.promptRelogin();
    });
    // Capgo rolls a live-update bundle back unless this runs within appReadyTimeout
    // (10 s) of launch, so it goes first, before any network call. Harmless where live
    // updates are off (capacitor.config.ts); rejects where the native plugin is not
    // linked (iOS until the next Mac `cap sync`).
    if (this.platformAdapter.isNative) {
      CapacitorUpdater.notifyAppReady().catch((err) => console.warn("CapacitorUpdater.notifyAppReady:", err));
      // Logs the crew out once a login is 18 h old and the iPad is parked and untouched,
      // after a cancellable 1 h countdown. The reload after it frees part of the WebContent
      // memory WebKit keeps.
      this.idleReload.start();
    }

    // Auto-arm bypass in dev builds: skip the manual login-page toggle.
    // Web build has its own login flow (ClientTokenService) and the
    // bypass-seed pipeline calls /query without a JWT — gate it off.
    // Auto-arm bypass in dev builds: skip the manual login-page toggle. An explicit
    // logout sets the suppress flag so this load lands on /login instead of
    // re-bypassing; a real login clears it (in AuthService.markLoginCleared — NOT
    // here, since authLogin_user hydrates async and is always false at construction).
    const bypassSuppressed = (() => {
      try {
        return localStorage.getItem("efb.suppressDevBypass") === "1";
      } catch {
        return false;
      }
    })();
    if (!environment.production && !this.auth.authLogin_user && this.platformAdapter.isNative && !bypassSuppressed) {
      this.auth.devBypassLogin = true;
    }

    // Web post-login bootstrap: WebSessionBootstrapService subscribes to
    // tokenObs itself (constructor side-effect) and serializes transitions
    // via concatMap. The subscription lives inside the service so the
    // per-token Promise can be recorded synchronously, ahead of the async
    // transition body — `readyForToken` (awaited by webAuthGuard + the
    // login page) then finds the matching transition immediately after
    // `setToken` returns, no BehaviorSubject filter race.

    // Flip dev bypass synchronously — otherwise scheduled update timers can
    // fire during the await below and abort with "user not logged in".
    const devBypass = !this.auth.authLogin_user && this.auth.devBypassLogin;
    if (devBypass) {
      this.opsControlSer.currMapServiceAircraft = DEV_BYPASS_AIRCRAFT;
      this.auth.markLoginCleared();
    }

    (async () => {
      try {
        // Notification polling is native-only — the web catalog opts out of
        // `notificationsNewCount` (per-registration tagPattern, would leak
        // cross-registration). NotificationsService no-ops on web, so the
        // initial call returns 0; skip the 60s interval entirely on web.
        if (this.platformAdapter.isNative) {
          await this.updateNotificationBatchCounter();
          setInterval(async () => {
            await this.updateNotificationBatchCounter();
          }, 60000);
        }

        if (devBypass) {
          this.opsControlSer.currMapServiceAircraft = await resolveDevBypassAircraft({
            focMain: this.opsControlSer.focMain,
            classCom,
          });
          this.menu.enable(true).catch((err) => console.error("menuEnable: ", err));
          await this.navCtrl.navigateRoot("/flow-briefing");
        } else if (this.platformAdapter.isNative) {
          // Native routing: persisted-session miss → /login, dev cold-start →
          // /updates so the user can verify what's installed. Both are
          // mobile-specific UX. On web, the router + `webAuthGuard` + the
          // login page's own redirect handle landing; the IIFE should not
          // auto-navigate or it'll clobber a deep link / fight the guard.
          if (!this.auth.authLogin_user) {
            console.log("oops we crashed, goto login page..");
            await this.navCtrl.navigateRoot("/login");
          } else if (!environment.production) {
            await this.navCtrl.navigateRoot("/updates");
          }
        }

        await this.platform.ready();

        this.settings.settings$.subscribe((val) => {
          this.generalSettings = val;
          this.settings.setLanguage(val.language);
          this.settings.navSource.next(val.navSource);
          this.navSource = this.settings.getNavSource();
        });

        await this.initializeApp();
        this.update.migrateUpdateHistory().catch((err) => console.warn("migrateUpdateHistory: ", err));
        await this.opsControlSer.getFuelOnBoardFromStorage();
        // StatusBar is native-only — on web setStyle rejects, which would
        // abort the rest of this try block (pause/resume wiring below).
        if (this.platform.is("hybrid")) {
          await StatusBar.setStyle({ style: Style.Default });
          console.log("style title bar default");
        }

        this.platform.pause.subscribe(() => {
          this.flightTimerCtrl.liveDetection_deviceIsForeground = false;
          this.platformPausedAt = new Date().getTime();
        });

        this.platform.resume.subscribe(async () => {
          try {
            const currDate = new Date().getTime();
            console.log("Turn ON the background-geolocation system");
            await this.geolocation.startLocationUpdates();
            console.log("-> start tracking");

            this.flightTimerCtrl.liveDetection_deviceIsForeground = true;
            await this.flightTimerCtrl.detectGpsBasedDepartureTimeAndFlightState();

            this.platformPausedAt = new Date().getTime();
          } catch (err) {
            console.error("platform resume: ", err);
          }
        });
      } catch (err) {
        console.error("constructor initialization error: ", err);
      }
    })();
  }

  ngOnInit(): void {
    this.acftType = this.opsControlSer.acftType;
  }

  public async showMenuReport(kind: "aircraft" | "crew"): Promise<void> {
    const registration = this.generalSettings?.registration;
    if (!registration || this.menuReportLoading) return;
    this.menuReportLoading = true;
    try {
      const htmlString =
        kind === "aircraft"
          ? await this.opsControlSer.focMain.getAircraftDataHtml(registration)
          : await this.opsControlSer.focMain.getCrewHtml(new Date(), registration);
      this.reportNavigation.setParameters({ htmlString });
      await this.menu.close();
      await this.navCtrl.navigateForward("/md-preview");
    } catch (error) {
      console.error("Menu report:", error);
      const alert = await this.alert.create({
        header: "Report unavailable",
        message: "Please try again.",
        buttons: ["OK"],
      });
      await alert.present();
    } finally {
      this.menuReportLoading = false;
    }
  }

  // The login found another aircraft, or the first one. The old aircraft's data
  // must go before the new binding is used, so this alert has no way around it.
  private async promptRebind(): Promise<void> {
    const alert = await this.alert.create({
      header: this.translate.instant("BindingResetTitle"),
      message: this.translate.instant("BindingResetText"),
      backdropDismiss: false,
      buttons: [
        { text: this.translate.instant("BindingResetButton"), role: "reset" },
        { text: this.translate.instant("BindingResetDecline"), role: "decline" },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role === "decline") {
      // Nothing is deleted. The next login finds the other aircraft again.
      this.auth.declineRebind();
      await this.auth.logout_user();
      window.location.reload();
      return;
    }
    // Only the reset button deletes. Anything else that closed the alert asks again.
    if (role !== "reset") {
      void this.promptRebind();
      return;
    }
    try {
      await this.auth.resetForRebind();
      window.location.reload();
    } catch (err) {
      console.error("binding reset failed: ", err);
      const failed = await this.alert.create({
        header: this.translate.instant("BindingResetTitle"),
        message: this.translate.instant("BindingResetFailed") + " " + err,
        buttons: ["OK"],
      });
      await failed.present();
      await failed.onDidDismiss();
      void this.promptRebind();
    }
  }

  // Two causes: the renewal found another aircraft for this device, or a
  // logged-in device holds no binding token. Sync stays off until the crew
  // logs in again. The alert waits for the landing, and the logout keeps the
  // ad-hoc duty: a start during a flight must not cost the crew its signed duty.
  private reloginWaiting = false;

  // After a restart the timer is restored only when the flow briefing opens, so
  // the crash-recovery marker counts too. The 3 h limit is the timer's own.
  private async flightInProgress(): Promise<boolean> {
    if (this.flightTimerCtrl.isTimerRunning()) return true;
    const started = await this.crashRecovery.getFlightTimerStartedAt();
    return !!started?.time && Date.now() - started.time < 3 * 60 * 60 * 1000;
  }

  private async promptRelogin(): Promise<void> {
    if (this.reloginWaiting) return;
    this.reloginWaiting = true;
    try {
      for (;;) {
        while (await this.flightInProgress()) {
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
        // A login during the wait already brought the token.
        if (!this.auth.reloginNeeded()) return;
        const alert = await this.alert.create({
          header: this.translate.instant("BindingReloginTitle"),
          message: this.translate.instant("BindingReloginText"),
          buttons: [
            { text: this.translate.instant("BindingReloginButton"), role: "1" },
            { text: this.translate.instant("BindingReloginLater"), role: "0" },
          ],
        });
        await alert.present();
        const { role } = await alert.onDidDismiss();
        // The alert can stay open through a login.
        if (role !== "1" || !this.auth.reloginNeeded()) return;
        // It can stay open through a takeoff too: wait for the landing and ask again.
        if (await this.flightInProgress()) continue;
        await this.auth.logout_user();
        window.location.reload();
        return;
      }
    } finally {
      this.reloginWaiting = false;
    }
  }

  public async logout(): Promise<void> {
    try {
      const alert = await this.alert.create({
        header: "Logout",
        message: "sure?",
        buttons: [
          { text: "yes log me out", role: "1" },
          { text: "no", role: "0" },
        ],
      });

      await alert.present();
      const { role } = await alert.onDidDismiss();

      if (role === "1") {
        // A logout drops the duty row of this device only, so the next user has to
        // sign. The crew rows stay: their delete syncs to every device of the aircraft.
        try {
          await new fccProvider().dropLocalAdhocDutyRow(classCom);
        } catch (err) {
          console.error("logout: duty row not dropped: ", err);
        }
        await this.auth.logout_user();
        window.location.reload();
      }
    } catch (err) {
      console.error("logout error: ", err);
    }
  }

  public async updateNotificationBatchCounter(): Promise<void> {
    try {
      this.numberOfNotifications = await this.notifications.getNumberOfNewNotifications();
      const notificationsPage = this.appPages.find((p) => p.url === "/notifications");
      if (notificationsPage) {
        notificationsPage.numberBatch = this.numberOfNotifications;
      }
    } catch (err) {
      console.error("updateNotificationBatchCounter: ", err);
    }
  }

  /**
   * Routes an `efbmax://` deep link into the Angular router.
   *
   * Ported from `main`, which the auth port dropped: auth-api mails
   * `efbmax://confirm?token=...`, `efbmax://unlock?token=...` and
   * `efbmax://password/new?token=...`, and those four routes are reachable by
   * NO other means. Without this listener the whole set was dead code.
   *
   * Native only -- on web the browser handles the URL and there is no scheme.
   */
  private handleDeepLinks(): void {
    if (!this.platformAdapter.isNative) return;

    void App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url: string = event.url;
        if (!url) return;
        try {
          // Strip the scheme; what remains is the in-app path plus its query.
          const path: string = url.replace(/^.*?:\/\//, "");
          void this.router.navigateByUrl(path);
        } catch (error: unknown) {
          console.error("Invalid deep-link URL received: ", url, error);
        }
      });
    });
  }

  private async initializeApp(): Promise<void> {
    try {
      console.log("initializeApp");

      this.handleDeepLinks();

      let lastDistFilter = 2;
      let lastLocation;

      setInterval(async () => {
        this.navSource = this.settings.getNavSource();
        if (this.navSource === navSourceEnum.neway_simcore) {
          try {
            const el = await this.geolocation.fetchSimulatorGeoLocation();

            if (!Number.isNaN(el.latitude) && !Number.isNaN(el.longitude)) {
              await this.flightTimerCtrl.addFlightMonitor_timerStartDetection_value(
                el.speed,
                el.latitude,
                el.longitude
              );
              this.settings.logPush(`simulator position received: ${el.latitude} ${el.longitude}`);
            }
            console.log("neway_simcore geolocation: ", el);
          } catch (err) {
            console.error("fetchSimulatorGeoLocation: ", err);
          }
        } else if (this.navSource === navSourceEnum.x_plane) {
          try {
            await this.geolocation.startXPlaneLocationUpdates();
          } catch (error) {
            console.error("startXPlaneLocationUpdates error: ", error);
          }
        }
      }, 500);

      let lastCenter = [];
      this.geolocation.geolocation$.subscribe((evt) => {
        if (evt.valid) {
          if (lastCenter.length > 0) {
            // Add distance calculation if needed
          }
          lastCenter = [evt.longitude, evt.latitude];
        }
      });

      setTimeout(async () => {
        try {
          await SplashScreen.hide();
          console.log("splash screen hide");
        } catch (err) {
          console.error("SplashScreen hide: ", err);
        }
      }, 500);
    } catch (err) {
      console.error("initializeApp error: ", err);
    }
  }
}

// Per browser, so every frame of a wallboard that shows the app keeps the choice across reloads.
const MENU_COLLAPSED_KEY = "efb.menuCollapsed";

function readMenuCollapsed(): boolean {
  try {
    return localStorage.getItem(MENU_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}
